/**
 * Skeleton loading component for Overview dashboard.
 * Shows animated placeholders with shimmer effect while core data is loading.
 */

// Inline keyframes for shimmer animation (no Tailwind config needed)
const shimmerStyle = {
  animation: 'shimmer 1.5s infinite',
}

// Add keyframes to document if not already present
if (typeof document !== 'undefined' && !document.getElementById('shimmer-keyframes')) {
  const style = document.createElement('style')
  style.id = 'shimmer-keyframes'
  style.textContent = `
    @keyframes shimmer {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }
  `
  document.head.appendChild(style)
}

// Shimmer effect component - creates a moving gradient highlight
const Shimmer = ({ className = '', children, style: customStyle = {} }) => (
  <div className={`relative overflow-hidden ${className}`} style={customStyle}>
    {children}
    <div 
      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
      style={{ ...shimmerStyle, transform: 'translateX(-100%)' }}
    />
  </div>
)

// Skeleton placeholder bar with shimmer
const SkeletonBar = ({ width = 'w-full', height = 'h-4', className = '' }) => (
  <Shimmer className={`${width} ${height} bg-gray-200 rounded ${className}`}>
    <div className={`${width} ${height}`} />
  </Shimmer>
)

// Skeleton placeholder circle
const SkeletonCircle = ({ size = 'h-6 w-6', className = '' }) => (
  <Shimmer className={`${size} bg-gray-200 rounded-full ${className}`}>
    <div className={size} />
  </Shimmer>
)

const OperatorCardSkeleton = ({ delay = 0 }) => (
  <div 
    className="bg-white rounded-xl border border-brand-gray-200 shadow-sm flex flex-col"
    style={{ animationDelay: `${delay}ms` }}
  >
    {/* Header */}
    <div className="p-4 flex justify-between items-center border-b border-brand-gray-200">
      <SkeletonBar width="w-24" height="h-6" className="rounded-full" />
      <SkeletonBar width="w-16" height="h-6" className="rounded-full" />
    </div>

    {/* Content */}
    <div className="p-4 space-y-3 flex-grow">
      {/* Row 1: 3 metrics */}
      <div className="grid grid-cols-3 gap-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="text-center">
            <SkeletonBar width="w-16" height="h-3" className="mx-auto mb-2" />
            <SkeletonBar width="w-12" height="h-6" className="mx-auto" />
          </div>
        ))}
      </div>

      {/* Row 2: 3 metrics */}
      <div className="grid grid-cols-3 gap-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="text-center">
            <SkeletonBar width="w-16" height="h-3" className="mx-auto mb-2" />
            <SkeletonBar width="w-12" height="h-6" className="mx-auto" />
          </div>
        ))}
      </div>

      {/* Row 3: 2 metrics */}
      <div className="grid grid-cols-2 gap-3">
        {[1, 2].map(i => (
          <div key={i} className="text-center">
            <SkeletonBar width="w-16" height="h-3" className="mx-auto mb-2" />
            <SkeletonBar width="w-12" height="h-6" className="mx-auto" />
          </div>
        ))}
      </div>

      {/* KAM Implementation section placeholder */}
      <Shimmer className="h-32 bg-gradient-to-br from-gray-100 to-gray-50 rounded-xl">
        <div className="p-4 space-y-2">
          <SkeletonBar width="w-32" height="h-4" />
          <SkeletonBar width="w-full" height="h-3" />
          <SkeletonBar width="w-full" height="h-3" />
          <SkeletonBar width="w-3/4" height="h-3" />
        </div>
      </Shimmer>

      {/* Bottom grid - Premiowanie & Plan */}
      <div className="grid grid-cols-2 gap-3">
        <Shimmer className="h-16 bg-gray-100 rounded-lg">
          <div className="p-3 text-center">
            <SkeletonBar width="w-20" height="h-3" className="mx-auto mb-2" />
            <SkeletonBar width="w-12" height="h-5" className="mx-auto" />
          </div>
        </Shimmer>
        <Shimmer className="h-16 bg-gray-100 rounded-lg">
          <div className="p-3 text-center">
            <SkeletonBar width="w-20" height="h-3" className="mx-auto mb-2" />
            <SkeletonBar width="w-12" height="h-5" className="mx-auto" />
          </div>
        </Shimmer>
      </div>
    </div>

    {/* Footer */}
    <div className="mt-auto border-t border-brand-gray-200 bg-brand-gray-50 rounded-b-xl p-4">
      <SkeletonBar width="w-48" height="h-4" className="mx-auto" />
    </div>
  </div>
)

// Banner card skeleton
const BannerCardSkeleton = ({ delay = 0 }) => (
  <Shimmer 
    className="rounded-lg border border-gray-200 p-4 flex items-center space-x-4 bg-gray-50"
    style={{ animationDelay: `${delay}ms` }}
  >
    <SkeletonCircle size="h-11 w-11" />
    <div className="flex-1 space-y-2">
      <SkeletonBar width="w-32" height="h-4" />
      <SkeletonBar width="w-48" height="h-3" />
    </div>
  </Shimmer>
)

const OverviewSkeleton = () => (
  <div className="space-y-6">
    {/* Header */}
    <div className="flex items-center justify-between">
      <div className="flex items-center">
        <SkeletonCircle size="h-6 w-6" className="mr-2" />
        <SkeletonBar width="w-48" height="h-8" />
      </div>
      <SkeletonBar width="w-28" height="h-10" className="rounded-lg" />
    </div>

    {/* Summary Banner - 4 cards */}
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
      {[0, 1, 2, 3].map(i => (
        <BannerCardSkeleton key={i} delay={i * 100} />
      ))}
    </div>

    {/* Data Freshness Header placeholder */}
    <Shimmer className="h-12 bg-white rounded-lg border border-brand-gray-200">
      <div className="p-3 flex items-center justify-center space-x-4">
        <SkeletonBar width="w-32" height="h-4" />
        <SkeletonBar width="w-24" height="h-4" />
        <SkeletonBar width="w-24" height="h-4" />
      </div>
    </Shimmer>

    {/* Section Header */}
    <div className="flex items-center">
      <SkeletonCircle size="h-5 w-5" className="mr-2" />
      <SkeletonBar width="w-40" height="h-6" />
    </div>

    {/* Operator Cards Grid - staggered animation */}
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
      {[0, 1, 2, 3].map(i => (
        <OperatorCardSkeleton key={i} delay={i * 150} />
      ))}
    </div>
  </div>
)

export default OverviewSkeleton
